<template>
  <div class="pa-4">
    <div class="d-flex align-center mb-4">
      <h1 class="text-h5 mr-4">System Health</h1>
      <v-btn small color="primary" :loading="loading" @click="onRunCheck">
        Run check now
      </v-btn>
    </div>

    <v-row>
      <v-col
        v-for="item in statusCards"
        :key="item.targetName"
        cols="12"
        sm="6"
        md="4"
        lg="3"
      >
        <v-card outlined>
          <v-card-text>
            <div class="d-flex align-center justify-space-between">
              <span class="font-weight-medium">{{ item.targetName }}</span>
              <v-chip
                small
                :color="statusColor(item.status)"
                text-color="white"
              >
                {{ item.status || 'unknown' }}
              </v-chip>
            </div>
            <div
              class="grey--text text--darken-1 mt-1"
              style="font-size: 13px;"
            >
              <expandable-text :text="item.message" />
            </div>
            <div class="grey--text mt-1" style="font-size: 11px;">
              {{
                item.checkedAt ? new Date(item.checkedAt).toLocaleString() : ''
              }}
            </div>
            <v-btn
              v-if="item.restartable"
              x-small
              text
              color="error"
              class="mt-2 pa-0"
              @click="onRestart(item.targetName)"
            >
              Restart
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <h2 class="text-h6 mt-6 mb-2">Machine resources</h2>
    <v-row>
      <v-col
        v-for="item in resourceCards"
        :key="item.targetName"
        cols="12"
        sm="6"
        md="3"
      >
        <div class="mb-1">
          {{ item.targetName.replace('resource:', '') }}
          <span class="grey--text">— {{ item.message }}</span>
        </div>
        <v-progress-linear
          :value="resourceValue(item)"
          height="10"
          :color="statusColor(item.status)"
          rounded
        />
        <div class="grey--text mt-2" style="font-size: 11px;">
          {{ item.targetName.replace('resource:', '') }} (%) — last
          {{ sparklineValues(item.targetName).length }} samples (plotted latest:
          {{ sparklineValues(item.targetName).slice(-1)[0] }}, raw latest:
          {{
            resourceEntries(item.targetName).slice(-1)[0] &&
              resourceEntries(item.targetName).slice(-1)[0].value
          }})
        </div>
        <div class="d-flex mt-1">
          <div
            class="d-flex flex-column justify-space-between grey--text text-right mr-2"
            style="font-size: 10px; width: 32px; height: 120px;"
          >
            <span>100%</span>
            <span>50%</span>
            <span>0%</span>
          </div>
          <svg
            class="flex-grow-1"
            :class="statusColor(item.status) + '--text'"
            viewBox="0 0 300 120"
            preserveAspectRatio="none"
            style="width: 100%; height: 120px; display: block;"
          >
            <polyline
              :points="fixedScalePoints(item.targetName)"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            />
          </svg>
        </div>
        <div
          class="grey--text d-flex justify-space-between mt-1"
          style="font-size: 10px; padding-left: 40px;"
        >
          <span>{{ sparklineTimeRange(item.targetName).start }}</span>
          <span>{{ sparklineTimeRange(item.targetName).end }}</span>
        </div>
      </v-col>
    </v-row>

    <h2 class="text-h6 mt-6 mb-2">Synthetic render check</h2>
    <div class="grey--text mb-2" style="font-size: 12px;">
      Headless-browser check that a masterplan/building actually renders and is
      clickable — runs on "Run check now" above, and every 30 minutes
      automatically wherever the monitor cron is enabled.
    </div>
    <v-data-table
      :headers="syntheticHeaders"
      :items="syntheticLogEntries"
      item-key="checkedAt"
      :items-per-page="10"
      no-data-text="No synthetic check has run yet"
    >
      <template v-slot:item.targetName="{ item }">
        {{ item.targetName.replace('synthetic:', '') }}
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip small :color="statusColor(item.status)" text-color="white">
          {{ item.status === 'ok' ? 'passed' : 'failed' }}
        </v-chip>
      </template>
      <template v-slot:item.message="{ item }">
        <div style="max-width: 480px;">
          <expandable-text :text="item.message" :threshold="80" />
        </div>
      </template>
      <template v-slot:item.checkedAt="{ item }">
        {{ item.checkedAt ? new Date(item.checkedAt).toLocaleString() : '' }}
      </template>
    </v-data-table>

    <h2 class="text-h6 mt-6 mb-2">Alert recipients</h2>
    <v-row>
      <v-col cols="12" md="8">
        <v-combobox
          v-model="editableRecipients"
          :search-input.sync="recipientSearchInput"
          label="Emails notified on downtime"
          hint="Press Enter after typing each address"
          persistent-hint
          multiple
          chips
          deletable-chips
          small-chips
          hide-selected
        />
        <div
          v-if="!$store.state.HealthStore.alertRecipients.length"
          class="warning--text mb-2"
          style="font-size: 12px;"
        >
          No recipients configured — critical failures will NOT be emailed to
          anyone until at least one address is added and saved here.
        </div>
        <v-btn
          small
          color="primary"
          :loading="savingRecipients"
          @click="onSaveRecipients"
        >
          Save recipients
        </v-btn>
        <v-switch
          class="mt-4"
          :input-value="$store.state.HealthStore.emailsSuppressed"
          label="Don't send alert emails"
          color="error"
          @change="onToggleSuppressEmails"
        />
        <div
          v-if="$store.state.HealthStore.emailsSuppressed"
          class="error--text"
          style="font-size: 12px;"
        >
          Alert emails are currently suppressed — critical failures will NOT be
          emailed to anyone until this is turned back off.
        </div>
      </v-col>
    </v-row>

    <h2 class="text-h6 mt-6 mb-2">Incident history</h2>
    <v-data-table
      :headers="incidentHeaders"
      :items="$store.state.HealthStore.incidents"
      item-key="targetName"
      :items-per-page="10"
    >
      <template v-slot:item.status="{ item }">
        <v-chip small :color="statusColor(item.status)" text-color="white">
          {{ item.status }}
        </v-chip>
      </template>
      <template v-slot:item.openedMessage="{ item }">
        <div style="max-width: 260px;">
          <expandable-text
            :text="item.openedMessage || item.lastMessage"
            :threshold="60"
          />
        </div>
      </template>
      <template v-slot:item.lastMessage="{ item }">
        <div style="max-width: 260px;">
          <expandable-text :text="item.lastMessage" :threshold="60" />
        </div>
      </template>
      <template v-slot:item.restartAttempted="{ item }">
        {{ item.restartAttempted ? item.restartOutcome || 'yes' : 'no' }}
      </template>
      <template v-slot:item.openedAt="{ item }">
        {{ item.openedAt ? new Date(item.openedAt).toLocaleString() : '' }}
      </template>
      <template v-slot:item.resolvedAt="{ item }">
        {{ item.resolvedAt ? new Date(item.resolvedAt).toLocaleString() : '-' }}
      </template>
      <template v-slot:item.recoveryMessage="{ item }">
        <div style="max-width: 260px;">
          <expandable-text :text="item.recoveryMessage" :threshold="60" />
        </div>
      </template>
      <template v-slot:item.durationOpenMs="{ item }">
        {{ formatDuration(item.durationOpenMs) }}
      </template>
    </v-data-table>

    <h2 class="text-h6 mt-6 mb-2">Request log</h2>
    <div class="grey--text mb-2" style="font-size: 12px;">
      Metadata only (method, path, IP, status, duration, request/response size,
      user agent, referer, user) — no request bodies or geo-IP are captured. IP
      is only taken from X-Forwarded-For when the request came through a trusted
      proxy, otherwise the real connecting address is logged. Entries older than
      {{ logRetentionDays }} days are purged automatically.
    </div>
    <v-row dense>
      <v-col cols="6" sm="2">
        <v-select
          v-model="requestFilters.category"
          :items="categoryOptions"
          label="Section"
          clearable
          dense
        />
      </v-col>
      <v-col cols="6" sm="2">
        <v-select
          v-model="requestFilters.method"
          :items="methodOptions"
          label="Method"
          clearable
          dense
        />
      </v-col>
      <v-col cols="6" sm="2">
        <v-text-field
          v-model="requestFilters.status"
          label="Status code"
          clearable
          dense
        />
      </v-col>
      <v-col cols="6" sm="2">
        <v-text-field
          v-model="requestFilters.userId"
          label="User id"
          clearable
          dense
        />
      </v-col>
      <v-col cols="12" sm="3">
        <v-text-field
          v-model="requestFilters.pathContains"
          label="Path contains"
          clearable
          dense
        />
      </v-col>
      <v-col cols="12" sm="1" class="d-flex align-center">
        <v-btn small text color="primary" @click="onFilterRequests">
          Filter
        </v-btn>
      </v-col>
    </v-row>
    <v-data-table
      :headers="requestLogHeaders"
      :items="$store.state.HealthStore.requestLogs"
      item-key="loggedAt"
      :items-per-page="15"
    >
      <template v-slot:item.category="{ item }">
        <v-chip small outlined>{{ item.category }}</v-chip>
      </template>
      <template v-slot:item.statusCode="{ item }">
        <span :class="item.statusCode >= 400 ? 'error--text' : ''">
          {{ item.statusCode }}
        </span>
      </template>
      <template v-slot:item.requestBytes="{ item }">
        {{ formatBytes(item.requestBytes) }}
      </template>
      <template v-slot:item.responseBytes="{ item }">
        {{ formatBytes(item.responseBytes) }}
      </template>
      <template v-slot:item.userAgent="{ item }">
        <div style="max-width: 220px;">
          <expandable-text :text="item.userAgent" :threshold="40" />
        </div>
      </template>
      <template v-slot:item.referer="{ item }">
        <div style="max-width: 180px;">
          <expandable-text :text="item.referer" :threshold="30" />
        </div>
      </template>
      <template v-slot:item.loggedAt="{ item }">
        {{ new Date(item.loggedAt).toLocaleString() }}
      </template>
    </v-data-table>

    <h2 class="text-h6 mt-6 mb-2">Error log</h2>
    <div class="grey--text mb-2" style="font-size: 12px;">
      Server-side errors — request errors caught by the API's error handler,
      plus uncaught exceptions and unhandled promise rejections anywhere in the
      process. Captures the error name, message, stack trace, and (when
      available) the request that triggered it. Entries older than
      {{ logRetentionDays }} days are purged automatically.
    </div>
    <v-row dense>
      <v-col cols="6" sm="2">
        <v-select
          v-model="errorFilters.source"
          :items="errorSourceOptions"
          label="Source"
          clearable
          dense
        />
      </v-col>
      <v-col cols="6" sm="2">
        <v-text-field
          v-model="errorFilters.statusCode"
          label="Status code"
          clearable
          dense
        />
      </v-col>
      <v-col cols="12" sm="4">
        <v-text-field
          v-model="errorFilters.pathContains"
          label="Path contains"
          clearable
          dense
        />
      </v-col>
      <v-col cols="12" sm="1" class="d-flex align-center">
        <v-btn small text color="primary" @click="onFilterErrors">
          Filter
        </v-btn>
      </v-col>
    </v-row>
    <v-data-table
      :headers="errorLogHeaders"
      :items="$store.state.HealthStore.errorLogs"
      item-key="loggedAt"
      :items-per-page="15"
      no-data-text="No errors logged"
    >
      <template v-slot:item.source="{ item }">
        <v-chip small outlined>{{ item.source }}</v-chip>
      </template>
      <template v-slot:item.message="{ item }">
        <div style="max-width: 320px;">
          <expandable-text :text="item.message" :threshold="80" />
        </div>
      </template>
      <template v-slot:item.stack="{ item }">
        <div style="max-width: 360px;">
          <expandable-text :text="item.stack" :threshold="60" />
        </div>
      </template>
      <template v-slot:item.loggedAt="{ item }">
        {{ new Date(item.loggedAt).toLocaleString() }}
      </template>
    </v-data-table>
  </div>
</template>

<script>
import ExpandableText from '../components/ExpandableText'

export default {
  components: {
    ExpandableText
  },
  data() {
    return {
      loading: false,
      savingRecipients: false,
      editableRecipients: [],
      recipientSearchInput: '',
      pollTimer: null,
      requestFilters: {
        category: null,
        method: null,
        status: '',
        userId: '',
        pathContains: ''
      },
      errorFilters: {
        source: null,
        statusCode: '',
        pathContains: ''
      },
      categoryOptions: [
        'admin',
        'frontend',
        'public',
        'health',
        'static',
        'other'
      ],
      methodOptions: ['GET', 'POST', 'PATCH', 'PUT', 'DELETE'],
      errorSourceOptions: [
        'restify',
        'uncaughtException',
        'unhandledRejection'
      ],
      // Mirrors HEALTH_LOG_RETENTION_DAYS' server-side default — purely
      // informational copy on this page, not read from a config endpoint.
      logRetentionDays: 7,
      syntheticHeaders: [
        { text: 'Target', value: 'targetName' },
        { text: 'Result', value: 'status' },
        { text: 'Details', value: 'message' },
        { text: 'Checked', value: 'checkedAt' }
      ],
      incidentHeaders: [
        { text: 'Target', value: 'targetName' },
        { text: 'Status', value: 'status' },
        { text: 'Cause', value: 'openedMessage' },
        { text: 'Consecutive failures', value: 'consecutiveFailures' },
        { text: 'Restart', value: 'restartAttempted' },
        { text: 'Latest detail', value: 'lastMessage' },
        { text: 'Opened', value: 'openedAt' },
        { text: 'Resolved', value: 'resolvedAt' },
        { text: 'Recovery', value: 'recoveryMessage' },
        { text: 'Duration', value: 'durationOpenMs' }
      ],
      requestLogHeaders: [
        { text: 'Time', value: 'loggedAt' },
        { text: 'Section', value: 'category' },
        { text: 'Method', value: 'method' },
        { text: 'Path', value: 'path' },
        { text: 'Status', value: 'statusCode' },
        { text: 'Duration (ms)', value: 'durationMs' },
        { text: 'IP', value: 'ip' },
        { text: 'User id', value: 'userId' },
        { text: 'Req size', value: 'requestBytes' },
        { text: 'Resp size', value: 'responseBytes' },
        { text: 'User agent', value: 'userAgent' },
        { text: 'Referer', value: 'referer' }
      ],
      errorLogHeaders: [
        { text: 'Time', value: 'loggedAt' },
        { text: 'Source', value: 'source' },
        { text: 'Name', value: 'name' },
        { text: 'Message', value: 'message' },
        { text: 'Status', value: 'statusCode' },
        { text: 'Method', value: 'method' },
        { text: 'Path', value: 'path' },
        { text: 'User id', value: 'userId' },
        { text: 'Stack', value: 'stack' }
      ]
    }
  },
  computed: {
    statusCards() {
      return this.$store.getters['HealthStore/statusList'].filter(
        item =>
          !item.targetName.startsWith('resource:') &&
          !item.targetName.startsWith('synthetic:')
      )
    },
    resourceCards() {
      return this.$store.getters['HealthStore/statusList'].filter(item =>
        item.targetName.startsWith('resource:')
      )
    },
    syntheticLogEntries() {
      const history = this.$store.state.HealthStore.syntheticHistory || {}
      return Object.values(history)
        .flat()
        .sort((a, b) => new Date(b.checkedAt) - new Date(a.checkedAt))
    }
  },
  watch: {
    '$store.state.HealthStore.alertRecipients': {
      immediate: true,
      handler(emails) {
        this.editableRecipients = (emails || []).slice()
      }
    }
  },
  created() {
    // Defense-in-depth: this app has no route-level role guard yet, so the
    // nav item hides this page but the route itself must also self-check.
    const role = this.$store.getters['UserStore/userRole']
    const userId =
      this.$store.state.UserStore.user && this.$store.state.UserStore.user.id
    if (role !== 'super_admin' || userId !== 1) {
      this.$router.push('/')
      return
    }
    this.$store.dispatch('HealthStore/fetchStatus')
    // Resources are sampled every ~10s server-side (HEALTH_RESOURCE_CHECK_INTERVAL_SECONDS);
    // 60 samples covers roughly the last 10 minutes of trend.
    this.$store.dispatch('HealthStore/fetchResourceHistory', 60)
    // Synthetic checks run at most every 30 min (cron) or on manual "Run
    // check now" — not worth polling on the same 10s cadence as resources.
    this.$store.dispatch('HealthStore/fetchSyntheticHistory')
    this.$store.dispatch('HealthStore/fetchAlertRecipients')
    this.$store.dispatch('HealthStore/fetchEmailsSuppressed')
    this.$store.dispatch('HealthStore/fetchRequestLogs')
    this.$store.dispatch('HealthStore/fetchErrorLogs')
    // Poll status + resource history to match the backend's sampling rate,
    // so the sparklines actually look live rather than static until reload.
    this.pollTimer = setInterval(() => {
      this.$store.dispatch('HealthStore/fetchStatus')
      this.$store.dispatch('HealthStore/fetchResourceHistory', 60)
    }, 10000)
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer)
  },
  methods: {
    statusColor(status) {
      if (status === 'ok') return 'success'
      if (status === 'warning') return 'warning'
      if (status === 'critical' || status === 'down') return 'error'
      return 'grey'
    },
    resourceValue(item) {
      const n = Number(item.value)
      return isNaN(n) ? 0 : Math.min(100, n)
    },
    resourceEntries(targetName) {
      const metric = targetName.replace('resource:', '')
      const entries =
        this.$store.state.HealthStore.resourceHistory[metric] || []
      // history is newest-first; reverse so the sparkline reads left-to-right
      // oldest-to-newest, like a normal trend chart.
      return entries.slice().reverse()
    },
    sparklineValues(targetName) {
      // Clamped to match the fixed 0-100% side axis drawn next to the chart —
      // without this a value like CPU's occasional >100 (load ratio over 1x
      // per core) would silently break the shared scale.
      return this.resourceEntries(targetName).map(e => {
        const n = Number(e.value) || 0
        return Math.min(100, Math.max(0, n))
      })
    },
    // v-sparkline has no min/max prop — it always auto-scales to the
    // VALUES array's own observed min/max (see genPoints() in
    // node_modules/vuetify/src/components/VSparkline/helpers/core.ts), so
    // :min="0" :max="100" were silently discarded the whole time
    // (inheritAttrs: false, and neither prop exists on the component).
    // When a metric barely moves across the sampled window, that auto-scale
    // degenerates — max-min collapses to ~0, and every point maps to the
    // same Y position (the bottom of the chart) regardless of the actual
    // constant value. A hand-rolled polyline against a real fixed 0-100
    // domain, matching the 100/50/0% labels already drawn beside it, sidesteps
    // this entirely. viewBox width is arbitrary (not pixels) since
    // preserveAspectRatio="none" stretches it to the actual rendered width.
    fixedScalePoints(targetName) {
      const values = this.sparklineValues(targetName)
      if (values.length < 2) return ''
      const width = 300
      const height = 120
      const step = width / (values.length - 1)
      return values
        .map(
          (v, i) =>
            `${(i * step).toFixed(1)},${(height - (v / 100) * height).toFixed(
              1
            )}`
        )
        .join(' ')
    },
    sparklineTimeRange(targetName) {
      const entries = this.resourceEntries(targetName)
      const fmt = e =>
        e && e.checkedAt
          ? new Date(e.checkedAt).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })
          : ''
      return {
        start: fmt(entries[0]),
        end: fmt(entries[entries.length - 1])
      }
    },
    async onSaveRecipients() {
      // v-combobox doesn't commit typed-but-not-yet-Entered text into the
      // chips array on its own — reported live: typing an address and
      // clicking Save directly (without pressing Enter first) silently
      // saved the previous list, dropping the new address entirely.
      // Treat any leftover search text as one more entry to add.
      const pending = (this.recipientSearchInput || '').trim()
      if (pending && !this.editableRecipients.includes(pending)) {
        this.editableRecipients = [...this.editableRecipients, pending]
      }
      this.recipientSearchInput = ''
      this.savingRecipients = true
      try {
        await this.$store.dispatch(
          'HealthStore/saveAlertRecipients',
          this.editableRecipients
        )
      } finally {
        this.savingRecipients = false
      }
    },
    onFilterRequests() {
      this.$store.dispatch('HealthStore/fetchRequestLogs', this.requestFilters)
    },
    onFilterErrors() {
      this.$store.dispatch('HealthStore/fetchErrorLogs', this.errorFilters)
    },
    formatBytes(n) {
      if (n === null || n === undefined || isNaN(n)) return '-'
      if (n < 1024) return `${n} B`
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
      return `${(n / (1024 * 1024)).toFixed(1)} MB`
    },
    formatDuration(ms) {
      if (ms === null || ms === undefined || isNaN(ms)) return '-'
      const seconds = Math.round(ms / 1000)
      if (seconds < 60) return `${seconds}s`
      const minutes = Math.floor(seconds / 60)
      if (minutes < 60) return `${minutes}m ${seconds % 60}s`
      const hours = Math.floor(minutes / 60)
      return `${hours}h ${minutes % 60}m`
    },
    async onRunCheck() {
      this.loading = true
      try {
        await this.$store.dispatch('HealthStore/runCheck')
      } finally {
        this.loading = false
      }
    },
    onRestart(processName) {
      // Two-step confirmation, deliberately: DialogStore's OK mutation calls
      // dialog.ok() and then unconditionally clears state.dialog right after
      // — opening the second dialog synchronously inside ok() would get
      // wiped out by that same clear. $nextTick defers it to run after the
      // first dialog's own cleanup has already happened.
      this.$store.dispatch('DialogStore/open', {
        title: 'Restart ' + processName + '?',
        text:
          'This will restart the live process (pm2 restart in prod/staging, or the equivalent dev.sh process locally).',
        okText: 'Restart',
        ok: () => {
          this.$nextTick(() => {
            this.$store.dispatch('DialogStore/open', {
              title: 'Are you sure?',
              text:
                'Restarting will interrupt any in-progress requests to this process right now — anyone actively using the Property Navigator or Backoffice against this environment may lose unsaved work. This cannot be undone.',
              okText: 'Yes, restart now',
              ok: () => {
                this.$store.dispatch('HealthStore/restartProcess', processName)
              },
              cancelText: 'No, cancel',
              cancel: () => {}
            })
          })
        },
        cancelText: 'Cancel',
        cancel: () => {}
      })
    },
    onToggleSuppressEmails(newValue) {
      const currentlySuppressed = this.$store.state.HealthStore.emailsSuppressed
      if (newValue === currentlySuppressed) return

      if (!newValue) {
        // Turning suppression back OFF restores normal alerting — the safe
        // direction, no need to gate it behind a confirmation.
        this.$store.dispatch('HealthStore/saveEmailsSuppressed', false)
        return
      }

      // Turning it ON is the dangerous direction — a real critical failure
      // could go completely unnoticed while this is active. Two-step
      // confirmation, same $nextTick-deferred chaining as onRestart above.
      this.$store.dispatch('DialogStore/open', {
        title: 'Suppress alert emails?',
        text:
          'While this is on, no one will be emailed if a critical failure happens on this environment.',
        okText: 'Continue',
        ok: () => {
          this.$nextTick(() => {
            this.$store.dispatch('DialogStore/open', {
              title: 'Are you sure?',
              text:
                'This means a real production or staging outage could go completely unnoticed while this is on, until someone manually checks the dashboard. Confirm you want to suppress all alert emails.',
              okText: 'Yes, suppress emails',
              ok: () => {
                this.$store.dispatch('HealthStore/saveEmailsSuppressed', true)
              },
              cancelText: 'No, keep emails on',
              cancel: () => {}
            })
          })
        },
        cancelText: 'Cancel',
        cancel: () => {}
      })
    }
  }
}
</script>

<style scoped></style>
